import { v4 as uuid } from 'uuid'
import { storageMMKV } from '@store'
import type { CheckInManifestTicket, ScanCheckInResult } from '@hooks'

const DEVICE_ID_KEY = 'event-checkin-device-id'
const manifestKey = (eventId: string) => `event-checkin-manifest:${eventId}`
const queueKey = (eventId: string) => `event-checkin-queue:${eventId}`
const META_PREFIX = 'event-checkin-meta:'
const metaKey = (eventId: string) => `${META_PREFIX}${eventId}`
/** Local copies are kept this long after the end of an event (late arrivals, a sync after the doors close). */
const KEEP_AFTER_END_MS = 48 * 3_600_000

export interface PendingCheckIn {
  clientId: string
  attendeeId: string
  accountId: string
  firstName: string
  lastName: string
  /** The original scan time on this device — kept even once synced. */
  checkedInAt: string
}

/** One id per device, generated once and kept forever: tells two organizer phones apart. */
export function getOfflineDeviceId(): string {
  const existing = storageMMKV.getString(DEVICE_ID_KEY)
  if (existing) return existing
  const id = uuid()
  storageMMKV.set(DEVICE_ID_KEY, id)
  return id
}

function readJson<T>(key: string, fallback: T): T {
  const raw = storageMMKV.getString(key)
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function loadManifest(eventId: string): CheckInManifestTicket[] {
  return readJson(manifestKey(eventId), [])
}

export function saveManifest(eventId: string, tickets: CheckInManifestTicket[], endsAt?: string): void {
  storageMMKV.set(manifestKey(eventId), JSON.stringify(tickets))
  if (endsAt) storageMMKV.set(metaKey(eventId), JSON.stringify({ endsAt }))
}

/** The four MMKV calls the purge needs (lets a test use an in-memory map). */
export interface CheckInStorage {
  getAllKeys(): string[]
  getString(key: string): string | undefined
  set(key: string, value: string): void
  delete(key: string): void
}

/**
 * Drops the local ticket list of every event over for more than 48 h. A
 * queue still holding unsynced check-ins is NEVER dropped (nor its meta, so
 * it is found again and purged once it has synced).
 */
export function purgeStaleCheckInData(now: Date = new Date(), storage: CheckInStorage = storageMMKV): void {
  for (const key of storage.getAllKeys()) {
    if (!key.startsWith(META_PREFIX)) continue
    const eventId = key.slice(META_PREFIX.length)
    let endsAt: number
    try {
      endsAt = new Date((JSON.parse(storage.getString(key) ?? '') as { endsAt: string }).endsAt).getTime()
    } catch {
      continue
    }
    if (!Number.isFinite(endsAt) || now.getTime() - endsAt <= KEEP_AFTER_END_MS) continue
    storage.delete(manifestKey(eventId))
    let queued: unknown[] = []
    try {
      queued = JSON.parse(storage.getString(queueKey(eventId)) ?? '[]') as unknown[]
    } catch {
      queued = [{ unreadable: true }]
    }
    if (Array.isArray(queued) && queued.length > 0) continue
    storage.delete(queueKey(eventId))
    storage.delete(key)
  }
}

export function loadQueue(eventId: string): PendingCheckIn[] {
  return readJson(queueKey(eventId), [])
}

export function saveQueue(eventId: string, queue: PendingCheckIn[]): void {
  storageMMKV.set(queueKey(eventId), JSON.stringify(queue))
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const PROFILE_PATH_RE = /\/profile\/([a-zA-Z0-9_-]+)/

/** Mirrors the API's `parseTicketCode`: pulls the attendeeId out, without a signature (offline can't verify one). */
export function parseOfflineTicketAttendeeId(code: string): string | null {
  const parts = code.trim().split('.')
  if (parts.length !== 2) return null
  const [attendeeId, signature] = parts
  if (!attendeeId || !signature || !UUID_RE.test(attendeeId)) return null
  return attendeeId
}

/** Mirrors the API's `parsePersonalQrAccountId` (the id is a profile id from the app's QR screen, or an account id). */
export function parseOfflinePersonalQrAccountId(code: string): string | null {
  const trimmed = code.trim()
  const match = trimmed.match(PROFILE_PATH_RE)
  const candidate = match ? match[1] : trimmed
  return UUID_RE.test(candidate) ? candidate : null
}

export interface OfflineMatchInput {
  code: string
  deviceId: string
  manifest: CheckInManifestTicket[]
  pending: PendingCheckIn[]
  now?: Date
}

export interface OfflineMatchOutcome {
  result: ScanCheckInResult
  /** Set only when this match created a brand new pending check-in. */
  newPending?: PendingCheckIn
}

/**
 * Offline equivalent of the API's check-in/scan: matched against the
 * downloaded manifest instead of verified against the HMAC secret (never
 * sent to the device). A ticket the manifest doesn't know is "invalid" —
 * exactly what a revoked or falsified ticket would also look like.
 */
export function matchOffline({ code, deviceId, manifest, pending, now = new Date() }: OfflineMatchInput): OfflineMatchOutcome {
  const ticketAttendeeId = parseOfflineTicketAttendeeId(code)
  const accountId = ticketAttendeeId ? null : parseOfflinePersonalQrAccountId(code)
  if (!ticketAttendeeId && !accountId) return { result: { status: 'invalid' } }

  const ticket = ticketAttendeeId
    ? manifest.find((entry) => entry.attendeeId === ticketAttendeeId)
    : manifest.find((entry) => entry.accountId === accountId || entry.profileId === accountId)

  if (!ticket) {
    // A personal QR unknown to the manifest: offline, there is no way to
    // register a walk-in (that needs the server), so never offer to.
    if (accountId) return { result: { status: 'not_registered', accountId, canRegister: false } }
    return { result: { status: 'invalid' } }
  }

  const queued = pending.find((item) => item.attendeeId === ticket.attendeeId)
  if (ticket.checkedInAt || queued) {
    const checkedInAt = queued?.checkedInAt ?? ticket.checkedInAt ?? now.toISOString()
    const checkedInDeviceId = queued ? deviceId : ticket.checkedInDeviceId
    return {
      result: {
        status: 'already_checked_in',
        attendeeId: ticket.attendeeId,
        accountId: ticket.accountId,
        firstName: ticket.firstName,
        lastName: ticket.lastName,
        checkedInAt,
        checkedInDeviceId,
        sameDevice: checkedInDeviceId === deviceId,
      },
    }
  }

  const checkedInAt = now.toISOString()
  const newPending: PendingCheckIn = {
    clientId: uuid(),
    attendeeId: ticket.attendeeId,
    accountId: ticket.accountId,
    firstName: ticket.firstName,
    lastName: ticket.lastName,
    checkedInAt,
  }
  return {
    result: {
      status: 'checked_in',
      attendeeId: ticket.attendeeId,
      accountId: ticket.accountId,
      firstName: ticket.firstName,
      lastName: ticket.lastName,
      checkedInAt,
      checkedInDeviceId: deviceId,
      sameDevice: true,
    },
    newPending,
  }
}
